import { useId, useMemo, useState } from "react";
import type { UserTag, TagVocabulary } from "../api/types";

// The tagging control (spec-4 dec-2..6) — reused on the Users table, the org
// detail and the user detail. Shows an entity's tags as removable chips and an
// add control with GitLab-style autocomplete: the `owner` scope autocompletes
// from the known operators (dec-4); every other scope is free-text with reuse
// suggestions drawn from tags already in use. Pure-presentational: add/remove are
// delegated to the parent, which persists via /api/tags and refreshes.

const KNOWN_SCOPES = ["owner", "lifecycle", "flag", "segment"];

export function TagEditor({
  tags,
  vocabulary,
  ownerOptions,
  onAdd,
  onRemove,
  busy = false,
}: {
  tags: UserTag[];
  vocabulary: TagVocabulary["vocabulary"];
  ownerOptions: TagVocabulary["ownerOptions"];
  onAdd: (scope: string, value: string) => void | Promise<void>;
  onRemove: (scope: string, value: string) => void | Promise<void>;
  busy?: boolean;
}) {
  const [scope, setScope] = useState("owner");
  const [value, setValue] = useState("");
  const uid = useId();
  const scopeListId = `scopes-${uid}`;
  const valueListId = `values-${uid}`;

  const scopeOptions = useMemo(
    () => Array.from(new Set([...KNOWN_SCOPES, ...vocabulary.map((v) => v.scope)])).sort(),
    [vocabulary],
  );

  // Value autocomplete: operators for owner:, otherwise reuse the values already
  // seen in this scope.
  const valueOptions = useMemo(() => {
    if (scope === "owner") {
      return ownerOptions.map((o) => ({ value: o.email, label: o.name ?? o.email }));
    }
    return vocabulary
      .filter((v) => v.scope === scope)
      .map((v) => ({ value: v.value, label: v.value }));
  }, [scope, ownerOptions, vocabulary]);

  const submit = async () => {
    const s = scope.trim();
    const v = value.trim();
    if (!s || !v) return;
    await onAdd(s, v);
    setValue("");
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid="tag-editor">
      <div className="flex flex-wrap items-center gap-1">
        {tags.map((t) => (
          <span
            key={`${t.scope}:${t.value}`}
            data-testid="tag-chip"
            title={`tagged by ${t.taggedBy}${t.taggedByKind === "system" ? " (system)" : ""}`}
            className="inline-flex items-center gap-1 rounded-full border border-edge bg-surface px-2 py-0.5 text-xs text-secondary"
          >
            <span className="text-muted">{t.scope}:</span>
            {t.value}
            <button
              type="button"
              data-testid="tag-remove"
              aria-label={`Remove ${t.scope}:${t.value}`}
              disabled={busy}
              onClick={() => onRemove(t.scope, t.value)}
              className="text-muted hover:text-primary disabled:opacity-40"
            >
              ×
            </button>
          </span>
        ))}
        {tags.length === 0 && <span className="text-xs text-muted">No tags</span>}
      </div>

      <div className="flex items-center gap-1">
        <input
          list={scopeListId}
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          placeholder="scope"
          data-testid="tag-scope-input"
          className="w-20 rounded-md border border-edge bg-surface px-1.5 py-0.5 text-xs text-primary focus:border-accent focus:outline-none"
        />
        <datalist id={scopeListId}>
          {scopeOptions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <span className="text-muted text-xs">:</span>
        <input
          list={valueListId}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
          placeholder={scope === "owner" ? "operator" : "value"}
          data-testid="tag-value-input"
          className="w-28 rounded-md border border-edge bg-surface px-1.5 py-0.5 text-xs text-primary focus:border-accent focus:outline-none"
        />
        <datalist id={valueListId}>
          {valueOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </datalist>
        <button
          type="button"
          data-testid="tag-add"
          disabled={busy || !value.trim()}
          onClick={() => void submit()}
          className="rounded-md border border-edge px-2 py-0.5 text-xs text-secondary transition-colors hover:bg-card-hover disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );
}
